import { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { getLeaderboard } from '../services/leaderboardApi';
import { formatTime } from '../utils/formatters';

const CATEGORIES = [
  { id: 'streaks', label: 'Streaks', icon: '🔥' },
  { id: 'speed', label: 'Speed', icon: '⚡' },
  { id: 'efficiency', label: 'Efficiency', icon: '🎯' },
  { id: 'completions', label: 'Completions', icon: '📊' },
];

function Leaderboard() {
  const { user } = useContext(AuthContext);
  const [players, setPlayers] = useState([]);
  const [category, setCategory] = useState('streaks');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRank, setUserRank] = useState(null);
  const [userData, setUserData] = useState(null);
  const [minCompletions, setMinCompletions] = useState(null);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setIsLoading(true);
        const data = await getLeaderboard('all-time', category);
        setPlayers(data.players);
        setUserRank(data.userRank);
        setUserData(data.userData);
        setMinCompletions(data.minCompletions);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLeaderboard();
  }, [category]);

  const getRankDisplay = (index) => {
    if (index === 0) return <span className="text-xl">🥇</span>;
    if (index === 1) return <span className="text-xl">🥈</span>;
    if (index === 2) return <span className="text-xl">🥉</span>;
    return <span className="text-puzzle-text-secondary font-medium">{index + 1}</span>;
  };

  const getCategoryDescription = () => {
    switch (category) {
      case 'speed':
        return 'Fastest average completion times';
      case 'efficiency':
        return 'Fewest average moves to complete';
      case 'completions':
        return 'Most puzzles completed';
      case 'streaks':
      default:
        return 'Top players ranked by streak';
    }
  };

  const renderPlayerAvatar = (player) => (
    <div className="w-8 h-8 rounded-full bg-puzzle-muted overflow-hidden flex items-center justify-center flex-shrink-0">
      {player.avatar ? (
        <img
          src={player.avatar}
          alt={player.username}
          className="w-full h-full object-cover"
        />
      ) : (
        <span className="text-xs font-semibold text-puzzle-text-secondary">
          {player.username.slice(0, 2).toUpperCase()}
        </span>
      )}
    </div>
  );

  const renderStreaksTable = () => (
    <table className="w-full text-sm">
      <thead className="text-left text-puzzle-text-secondary border-b bg-puzzle-muted">
        <tr>
          <th className="py-3 px-4 w-16">Rank</th>
          <th className="py-3 px-4">Player</th>
          <th className="py-3 px-4 text-center">
            Longest
          </th>
          <th className="py-3 px-4 text-center hidden sm:table-cell">
            Current
          </th>
          <th className="py-3 px-4 text-center hidden sm:table-cell">Puzzles</th>
        </tr>
      </thead>
      <tbody>
        {players.map((player, index) => {
          const isCurrentUser = user?.username === player.username;
          const primaryStreak = player.longestStreak;
          const secondaryStreak = player.currentStreak;
          return (
            <tr
              key={player.username}
              className={`border-b border-puzzle-border ${isCurrentUser ? 'bg-puzzle-blue/10' : ''}`}
            >
              <td className="py-4 px-4">{getRankDisplay(index)}</td>
              <td className="py-4 px-4">
                <div className="flex items-center gap-3">
                  {renderPlayerAvatar(player)}
                  <Link to={`/profile/${player.username}`} className={`font-medium hover:underline ${isCurrentUser ? 'text-puzzle-blue' : 'text-puzzle-text'}`}>
                    {player.username}
                    {isCurrentUser && <span className="ml-2 text-xs text-puzzle-blue">(You)</span>}
                  </Link>
                </div>
              </td>
              <td className="py-4 px-4 text-center">
                <span className="inline-flex items-center gap-1 text-orange-500 font-bold text-lg">
                  🏆
                  {primaryStreak}
                </span>
              </td>
              <td className="py-4 px-4 text-center hidden sm:table-cell">
                {secondaryStreak > 0 ? (
                  <span className="inline-flex items-center gap-1 text-orange-500">
                    🔥
                    {secondaryStreak}
                  </span>
                ) : (
                  <span className="text-puzzle-text-secondary">0</span>
                )}
              </td>
              <td className="py-4 px-4 text-center hidden sm:table-cell text-puzzle-blue font-semibold">
                {player.totalCompletions}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderSpeedTable = () => (
    <table className="w-full text-sm">
      <thead className="text-left text-puzzle-text-secondary border-b bg-puzzle-muted">
        <tr>
          <th className="py-3 px-4 w-16">Rank</th>
          <th className="py-3 px-4">Player</th>
          <th className="py-3 px-4 text-center">Avg Time</th>
          <th className="py-3 px-4 text-center hidden sm:table-cell">Best</th>
          <th className="py-3 px-4 text-center hidden sm:table-cell">Puzzles</th>
        </tr>
      </thead>
      <tbody>
        {players.map((player, index) => {
          const isCurrentUser = user?.username === player.username;
          return (
            <tr
              key={player.username}
              className={`border-b border-puzzle-border ${isCurrentUser ? 'bg-puzzle-blue/10' : ''}`}
            >
              <td className="py-4 px-4">{getRankDisplay(index)}</td>
              <td className="py-4 px-4">
                <div className="flex items-center gap-3">
                  {renderPlayerAvatar(player)}
                  <Link to={`/profile/${player.username}`} className={`font-medium hover:underline ${isCurrentUser ? 'text-puzzle-blue' : 'text-puzzle-text'}`}>
                    {player.username}
                    {isCurrentUser && <span className="ml-2 text-xs text-puzzle-blue">(You)</span>}
                  </Link>
                </div>
              </td>
              <td className="py-4 px-4 text-center">
                <span className="inline-flex items-center gap-1 text-blue-500 font-bold text-lg">
                  ⚡
                  {formatTime(player.avgTime)}
                </span>
              </td>
              <td className="py-4 px-4 text-center hidden sm:table-cell text-green-600 font-semibold">
                {formatTime(player.bestTime)}
              </td>
              <td className="py-4 px-4 text-center hidden sm:table-cell text-puzzle-text-secondary">
                {player.totalCompletions}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderEfficiencyTable = () => (
    <table className="w-full text-sm">
      <thead className="text-left text-puzzle-text-secondary border-b bg-puzzle-muted">
        <tr>
          <th className="py-3 px-4 w-16">Rank</th>
          <th className="py-3 px-4">Player</th>
          <th className="py-3 px-4 text-center">Avg Moves</th>
          <th className="py-3 px-4 text-center hidden sm:table-cell">Best</th>
          <th className="py-3 px-4 text-center hidden sm:table-cell">Puzzles</th>
        </tr>
      </thead>
      <tbody>
        {players.map((player, index) => {
          const isCurrentUser = user?.username === player.username;
          return (
            <tr
              key={player.username}
              className={`border-b border-puzzle-border ${isCurrentUser ? 'bg-puzzle-blue/10' : ''}`}
            >
              <td className="py-4 px-4">{getRankDisplay(index)}</td>
              <td className="py-4 px-4">
                <div className="flex items-center gap-3">
                  {renderPlayerAvatar(player)}
                  <Link to={`/profile/${player.username}`} className={`font-medium hover:underline ${isCurrentUser ? 'text-puzzle-blue' : 'text-puzzle-text'}`}>
                    {player.username}
                    {isCurrentUser && <span className="ml-2 text-xs text-puzzle-blue">(You)</span>}
                  </Link>
                </div>
              </td>
              <td className="py-4 px-4 text-center">
                <span className="inline-flex items-center gap-1 text-purple-500 font-bold text-lg">
                  🎯
                  {player.avgMoves}
                </span>
              </td>
              <td className="py-4 px-4 text-center hidden sm:table-cell text-green-600 font-semibold">
                {player.bestMoves}
              </td>
              <td className="py-4 px-4 text-center hidden sm:table-cell text-puzzle-text-secondary">
                {player.totalCompletions}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderCompletionsTable = () => (
    <table className="w-full text-sm">
      <thead className="text-left text-puzzle-text-secondary border-b bg-puzzle-muted">
        <tr>
          <th className="py-3 px-4 w-16">Rank</th>
          <th className="py-3 px-4">Player</th>
          <th className="py-3 px-4 text-center">Total</th>
          <th className="py-3 px-4 text-center hidden sm:table-cell">Avg Time</th>
          <th className="py-3 px-4 text-center hidden sm:table-cell">Avg Moves</th>
        </tr>
      </thead>
      <tbody>
        {players.map((player, index) => {
          const isCurrentUser = user?.username === player.username;
          return (
            <tr
              key={player.username}
              className={`border-b border-puzzle-border ${isCurrentUser ? 'bg-puzzle-blue/10' : ''}`}
            >
              <td className="py-4 px-4">{getRankDisplay(index)}</td>
              <td className="py-4 px-4">
                <div className="flex items-center gap-3">
                  {renderPlayerAvatar(player)}
                  <Link to={`/profile/${player.username}`} className={`font-medium hover:underline ${isCurrentUser ? 'text-puzzle-blue' : 'text-puzzle-text'}`}>
                    {player.username}
                    {isCurrentUser && <span className="ml-2 text-xs text-puzzle-blue">(You)</span>}
                  </Link>
                </div>
              </td>
              <td className="py-4 px-4 text-center">
                <span className="inline-flex items-center gap-1 text-puzzle-blue font-bold text-lg">
                  📊
                  {player.totalCompletions}
                </span>
              </td>
              <td className="py-4 px-4 text-center hidden sm:table-cell text-puzzle-text-secondary">
                {player.avgTime > 0 ? formatTime(player.avgTime) : '-'}
              </td>
              <td className="py-4 px-4 text-center hidden sm:table-cell text-puzzle-text-secondary">
                {player.avgMoves > 0 ? player.avgMoves : '-'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderTable = () => {
    switch (category) {
      case 'speed':
        return renderSpeedTable();
      case 'efficiency':
        return renderEfficiencyTable();
      case 'completions':
        return renderCompletionsTable();
      case 'streaks':
      default:
        return renderStreaksTable();
    }
  };

  const renderUserRankSection = () => {
    if (!userRank || !userData) return null;

    const isCurrentUser = true;
    let primaryValue;
    let primaryIcon;
    let secondaryInfo;

    switch (category) {
      case 'speed':
        primaryValue = formatTime(userData.avgTime);
        primaryIcon = '⚡';
        secondaryInfo = `Best: ${formatTime(userData.bestTime)}`;
        break;
      case 'efficiency':
        primaryValue = userData.avgMoves;
        primaryIcon = '🎯';
        secondaryInfo = `Best: ${userData.bestMoves}`;
        break;
      case 'completions':
        primaryValue = userData.totalCompletions;
        primaryIcon = '📊';
        secondaryInfo = `Avg: ${formatTime(userData.avgTime)}`;
        break;
      case 'streaks':
      default:
        primaryValue = userData.longestStreak;
        primaryIcon = '🏆';
        secondaryInfo = `${userData.totalCompletions} completed`;
        break;
    }

    return (
      <div className="border-t-2 border-dashed border-puzzle-border bg-puzzle-blue/10 px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-puzzle-text-secondary font-medium">
              #
              {userRank}
            </span>
            {renderPlayerAvatar(userData)}
            <Link to={`/profile/${userData.username}`} className={`font-medium hover:underline ${isCurrentUser ? 'text-puzzle-blue' : 'text-puzzle-text'}`}>
              {userData.username}
              <span className="ml-2 text-xs text-puzzle-blue">(You)</span>
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-bold text-lg">
              {primaryIcon}
              {' '}
              {primaryValue}
            </span>
            <span className="text-sm text-puzzle-text-secondary hidden sm:inline">
              {secondaryInfo}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderLeaderboardContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-puzzle-blue" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-16">
          <p className="text-puzzle-danger mb-4">{error}</p>
          <button
            type="button"
            onClick={() => setCategory(category)}
            className="text-puzzle-blue hover:underline"
          >
            Try again
          </button>
        </div>
      );
    }

    if (players.length === 0) {
      return (
        <div className="text-center py-16 px-4">
          <p className="text-puzzle-text-secondary mb-2">
            No rankings yet for this category.
          </p>
          {minCompletions && (
            <p className="text-sm text-puzzle-text-secondary mb-4">
              (Requires at least
              {' '}
              {minCompletions}
              {' '}
              completions)
            </p>
          )}
          <Link
            to="/"
            className="text-puzzle-blue hover:underline"
          >
            Complete today&apos;s puzzle to get on the leaderboard!
          </Link>
        </div>
      );
    }

    return (
      <>
        <div className="overflow-x-auto">
          {renderTable()}
        </div>
        {renderUserRankSection()}
      </>
    );
  };

  return (
    <div className="min-h-screen bg-puzzle-background py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-puzzle-text mb-2">Leaderboard</h1>
          <p className="text-puzzle-text-secondary">
            {getCategoryDescription()}
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex justify-center mb-4 overflow-x-auto pb-2">
          <div className="inline-flex bg-puzzle-surface rounded-lg shadow p-1 gap-1">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCategory(c.id)}
                className={`px-3 sm:px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                  category === c.id
                    ? 'bg-puzzle-blue text-white'
                    : 'text-puzzle-text-secondary hover:text-puzzle-text hover:bg-puzzle-hover'
                }`}
              >
                <span className="mr-1">{c.icon}</span>
                <span className="hidden sm:inline">{c.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Min Completions Notice */}
        {minCompletions && players.length > 0 && (
          <div className="text-center text-sm text-puzzle-text-secondary mb-4">
            Requires at least
            {' '}
            {minCompletions}
            {' '}
            completions to rank
          </div>
        )}

        {/* Leaderboard Card */}
        <div className="bg-puzzle-surface rounded-lg shadow overflow-hidden">
          {renderLeaderboardContent()}
        </div>

        {/* Call to Action */}
        {!user && players.length > 0 && (
          <div className="mt-6 bg-puzzle-blue/10 border border-puzzle-blue/30 rounded-lg p-4 text-center">
            <p className="text-sm text-puzzle-blue">
              <Link to="/register" className="font-semibold underline hover:opacity-80">
                Create an account
              </Link>
              {' '}
              to track your stats and appear on the leaderboard!
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Leaderboard;
